import { useCallback, useMemo } from "react";
import { Box, Button, Tooltip, Select, MenuItem, FormControl, InputLabel, IconButton } from '@mui/material';
import { MaterialReactTable, createMRTColumnHelper, useMaterialReactTable } from "material-react-table";
import axiosInstance from "../../../api/axios";
import { useTheme } from '@mui/material/styles';
import { Refresh, Delete, GppGood } from "@mui/icons-material";
import dayjs from 'dayjs';
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { notifySuccess, notifyError } from "../../../components/common/toast/ToastFunc";

const columnHelper = createMRTColumnHelper();

const columns = [
    columnHelper.accessor("id", {
        header: "User ID",
        enableEditing: false,
        size: 50,
    }),
    columnHelper.accessor("role", {
        header: "Role",
        enableEditing: false,
        size: 50,
    }),
    columnHelper.accessor("first_name", {
        header: "F Name",
        size: 30,
    }),
    columnHelper.accessor("last_name", {
        header: "L Name",
        size: 50,
    }),
    columnHelper.accessor("email_id", {
        header: "Email ID",
        enableEditing: false,
        size: 50,
    }),
    columnHelper.accessor("dob", {
        header: "DOB",
        enableEditing: false,
        size: 100,
    }),
    columnHelper.accessor("mobile_no", {
        header: "Mobile No",
        enableEditing: false,
        size: 50,
    }),
    columnHelper.accessor("address", {
        header: "Address",
        size: 50,
    }),
    columnHelper.accessor("city", {
        header: "City",
        size: 50,
    }),
    columnHelper.accessor("state", {
        header: "State",
        size: 50,
    }),
    columnHelper.accessor("zip_code", {
        header: "Zip Code",
        size: 50,
    }),
];

const UserTable = (props) => {
    const { data, loading, actionLoading, setActionLoading, reFetch, setReFetch, userType, setUserType, isRequestList, isEmployeeUserList } = props;

    const theme = useTheme();

    const buttonTheme = useMemo(() => {
        return {
            color: theme.mainColor, fontSize: 13, fontWeight: "bold", borderRadius: 2, borderColor: theme.mainColor, height: "45px",
            '&:hover': {
                backgroundColor: theme.mainColor, color: 'white', fontSize: 13, fontWeight: "bold", borderRadius: 2, borderColor: theme.mainColor
            },
        }
    }, [theme.mainColor]);

    const selectTheme = useMemo(() => ({
        '& .MuiInputLabel-root': {
            color: 'grey',
            '&.Mui-focused': {
                color: theme.mainColor,
            },
        },
        '& .MuiOutlinedInput-root': {
            width: '130px',
            height: '45px',
            '& fieldset': {
                borderColor: 'grey',
                borderRadius: '8px',
            },
            '&:hover fieldset': {
                borderColor: theme.mainColor,
            },
            '&.Mui-focused fieldset': {
                borderColor: theme.mainColor,
            },
        },
    }), [theme.mainColor]);

    const types = useMemo(() => {
        return ['All', 'Customer', 'Merchant', 'Employee', 'Manager'];
    }, []);

    const handleExportRows = (rows) => {
        try {
            const doc = new jsPDF();
            const tableData = rows.map((row, index) => [
                row.original["id"],
                row.original["role"],
                row.original["first_name"],
                row.original["last_name"],
                row.original["email_id"],
                dayjs(row.original["dob"]).format('MM/DD/YYYY'),
                row.original["mobile_no"],
                row.original["address"],
                row.original["city"],
                row.original["state"],
                row.original["zip_code"],
            ]);

            const tableHeaders = [
                "User ID", "Role", "First Name", "Last Name", "Email ID", "DOB", "Mobile No", "Address", "City", "State", "Zip Code",
            ];

            autoTable(doc, {
                head: [tableHeaders],
                body: tableData,
            });

            doc.save("users_list.pdf");
        } catch (error) {
            notifyError("Something went wrong");
        }
    };

    const openDeleteConfirmModal = (row) => {
        if (window.confirm('Are you sure you want to delete this user?')) {
            deleteUser(row.original.id);
        }
    };

    const deleteUser = useCallback(async (id) => {
        setActionLoading(true);
        try {
            const response = await axiosInstance.post('/customer/account-summary/delete-user', { user_id: id });
            notifySuccess(response?.data?.message || "Success");
            setReFetch(!reFetch);
        } catch (error) {
            notifyError(error.message)
        } finally {
            setActionLoading(false);
        }
    }, [reFetch, setActionLoading, setReFetch]);

    const updateUser = useCallback(async ({ values, table }) => {
        setActionLoading(true);
        try {
            const response = await axiosInstance.post('/customer/user-info/update-profile', { ...values, user_id: values["id"] });
            notifySuccess(response?.data?.message || "Success");
            table.setEditingRow(null);
            setReFetch(!reFetch);
        } catch (error) {
            notifyError(error.message)
        } finally {
            setActionLoading(false);
        }
    }, [reFetch, setActionLoading, setReFetch]);

    const authorizeUser = useCallback(async (user_id) => {
        setActionLoading(true);
        try {
            const response = await axiosInstance.post('/auth/authorize-profile-transaction', { id: user_id, auth_type: "Profile" });
            notifySuccess(response?.data?.message || "Success");
            setReFetch(!reFetch);
        } catch (error) {
            notifyError(error.message || "Something went wrong");
        } finally {
            setActionLoading(false);
        }
    }, [reFetch, setActionLoading, setReFetch]);

    const table = useMaterialReactTable({
        columns,
        data,
        initialState: { columnVisibility: { id: false, address: false, city: false, state: false, zip_code: false } },
        state: { showLoadingOverlay: actionLoading || loading },
        muiCircularProgressProps: {
            color: "primary",
            thickness: 5,
            size: 55,
        },
        muiSkeletonProps: {
            animation: "pulse",
            height: 28,
        },
        enableEditing: true,
        enableRowActions: true,
        enableFullScreenToggle: false,
        enableRowSelection: true,
        paginationDisplayMode: "pages",
        muiPaginationProps: {
            rowsPerPageOptions: [5, 10, 15, 20, 30],
        },
        positionToolbarAlertBanner: "bottom",
        muiTableHeadCellProps: {
            sx: {
                fontSize: "18px",
            },
        },
        muiTableBodyCellProps: ({ row }) => ({
            sx: {
                fontSize: "15px",
                fontWeight: row.getIsSelected() ? "bold" : "normal",
            },
        }),

        onEditingRowSave: updateUser,

        renderRowActionMenuItems: ({ row }) => [
            <Tooltip key="delete" title='Delete User Acc' placement="left">
                <MenuItem onClick={() => openDeleteConfirmModal(row)} disabled={loading || actionLoading}>
                    <Delete sx={{ color: "grey", mr: 1.5 }} />
                    Delete
                </MenuItem>
            </Tooltip>,
            !isEmployeeUserList && !isRequestList && (
                <Tooltip key="authorize" title='Authorize User Acc' placement="left">
                    <MenuItem onClick={() => authorizeUser(row.original.id)} disabled={row.original.is_authorized || loading || actionLoading}>
                        <GppGood sx={{ color: "grey", mr: 1.5 }} />
                        {row.original.is_authorized ? 'Authorized' : 'Authorize'}
                    </MenuItem>
                </Tooltip>
            )
        ].filter(Boolean),

        renderTopToolbarCustomActions: ({ table }) => (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "15px",
                    padding: "5px",
                    flexWrap: "wrap",
                }}
            >
                <Tooltip arrow title='Refresh Data'>
                    <span>
                        <IconButton
                            disabled={loading || actionLoading}
                            onClick={() => setReFetch((prev) => !prev)}
                            sx={{ color: theme.mainColor }}
                        >
                            <Refresh />
                        </IconButton>
                    </span>
                </Tooltip>
                {!isRequestList && !isEmployeeUserList && <FormControl sx={{ ...selectTheme }}>
                    <InputLabel>User Type</InputLabel>
                    <Select
                        label="User Type"
                        name="userType"
                        value={userType}
                        onChange={(event) => setUserType(event.target.value)}
                        disabled={loading || actionLoading}
                    >
                        {types.map((type, index) => (
                            <MenuItem key={index} value={type}>{type}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                }
                <Button
                    disabled={
                        table.getPrePaginationRowModel().rows.length === 0 || loading || actionLoading
                    }
                    onClick={() => handleExportRows(table.getPrePaginationRowModel().rows)}
                    startIcon={<FileDownloadIcon />}
                    variant='outlined'
                    sx={{ ...buttonTheme }}
                >
                    Export All
                </Button>
                <Button
                    disabled={
                        (!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()) || loading || actionLoading
                    }
                    onClick={() => handleExportRows(table.getSelectedRowModel().rows)}
                    startIcon={<FileDownloadIcon />}
                    variant='outlined'
                    sx={{ ...buttonTheme }}
                >
                    Export Sel
                </Button>
            </Box >
        ),
    });

    return <MaterialReactTable table={table} />;
};

export default UserTable;
